/**
 * The drawing a child has not finished yet.
 *
 * Until now this existed only in memory: close the tab halfway through a
 * picture and it had never happened. A draft is written as the child draws and
 * restored when the room opens, so the only way to lose an unfinished drawing
 * is to deliberately tap "Start again".
 *
 * Drafts sit in localStorage rather than IndexedDB on purpose. A phone kills a
 * backgrounded tab without warning, and `pagehide` gives you one synchronous
 * moment to write — an async IndexedDB transaction started there frequently
 * never lands. localStorage is synchronous, so the last strokes survive.
 */

import type { Scene, SceneKind } from './scene.ts';
import { decodeScene, encodeScene } from './scene.ts';

const PREFIX = 'imagineplay.draft.';
const DEBOUNCE_MS = 700;

const keyOf = (kind: SceneKind) => `${PREFIX}${kind}`;

export function saveDraft(kind: SceneKind, scene: Scene): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (scene.strokes.length === 0) {
      localStorage.removeItem(keyOf(kind));
      return;
    }
    localStorage.setItem(keyOf(kind), JSON.stringify({ at: Date.now(), scene: encodeScene(scene) }));
  } catch {
    // A draft that will not fit is a lost convenience, not a lost picture —
    // whatever is on the canvas is still there, and Keep still works.
  }
}

export function loadDraft(kind: SceneKind): Scene | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(keyOf(kind));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const scene = decodeScene((parsed as Record<string, unknown>).scene);
    return scene && scene.strokes.length > 0 ? scene : null;
  } catch {
    return null;
  }
}

export function clearDraft(kind: SceneKind): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(keyOf(kind));
}

export interface DraftKeeper {
  /** Something changed — write soon. */
  touch(): void;
  /** Write now. */
  flush(): void;
  /** The child deliberately started again. */
  clear(): void;
  /** Detach listeners when the room closes. */
  stop(): void;
}

/**
 * Writes on a debounce while drawing, and immediately whenever the page is
 * about to be hidden or torn down — the debounce alone would lose the last
 * second of drawing every time a child locks the tablet.
 */
export function createDraftKeeper(kind: SceneKind, read: () => Scene): DraftKeeper {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const flush = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (!stopped) saveDraft(kind, read());
  };

  const onHide = () => {
    if (typeof document === 'undefined' || document.visibilityState === 'hidden') flush();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flush);
    window.addEventListener('visibilitychange', onHide);
    document.addEventListener('visibilitychange', onHide);
  }

  return {
    touch() {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(flush, DEBOUNCE_MS);
    },
    flush,
    clear() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      clearDraft(kind);
    },
    stop() {
      flush();
      stopped = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', flush);
        window.removeEventListener('visibilitychange', onHide);
        document.removeEventListener('visibilitychange', onHide);
      }
    },
  };
}
